import { type PlaybookRunFragment, useToolInvocationsChangedSubscription } from "@qw/graphql";
import { Button, Icon, Spinner } from "@qw/ui";
import { useCallback, useEffect, useState } from "react";

import { ChatHeader, type ChatHeaderProps } from "../chat/components/chat-header";
import { ChatScrollWrapper } from "../chat/components/chat-scroll-wrapper";
import { useChat } from "../chat/providers/chat-provider";
import { useToolDescription } from "../chat/providers/chat-tool-metadata-provider";
import { formatElapsedTime, getPlaybookRunStatusLabel, getRunningToolName } from "./compact-playbook-mode";

export interface CompactPlaybookRunningProps {
  activeRun: PlaybookRunFragment;
  header?: ChatHeaderProps;
  onPlaybookRunChanged: () => void;
  onShowLiveTranscript: () => void;
  rootRun: PlaybookRunFragment;
}

interface RestoringPlaybookTranscriptProps {
  header?: ChatHeaderProps;
  onComplete: () => void;
}

export const RestoringPlaybookTranscript = ({ header, onComplete }: RestoringPlaybookTranscriptProps) => {
  const { retryHistory } = useChat();

  useEffect(() => {
    let mounted = true;
    void retryHistory().finally(() => {
      if (mounted) {
        onComplete();
      }
    });
    return () => {
      mounted = false;
    };
  }, [onComplete, retryHistory]);

  return (
    <ChatScrollWrapper>
      <ChatHeader {...(header ?? {})} />
      <div className="flex flex-1 items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading the completed transcript...
      </div>
    </ChatScrollWrapper>
  );
};

export const CompactPlaybookRunning = ({
  activeRun,
  header,
  onPlaybookRunChanged,
  onShowLiveTranscript,
  rootRun,
}: CompactPlaybookRunningProps) => {
  const { isReadOnly, stop } = useChat();
  const [now, setNow] = useState(() => Date.now());
  const [isStopping, setIsStopping] = useState(false);

  useToolInvocationsChangedSubscription({
    variables: { playbookRunId: activeRun.id },
    onData: onPlaybookRunChanged,
  });

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const runningToolName = getRunningToolName(activeRun);
  const runningToolDescription = useToolDescription(runningToolName ?? "");

  const handleStop = useCallback(async () => {
    setIsStopping(true);
    try {
      await stop();
      onPlaybookRunChanged();
    } finally {
      setIsStopping(false);
    }
  }, [onPlaybookRunChanged, stop]);

  return (
    <ChatScrollWrapper>
      <ChatHeader {...(header ?? {})} />
      <main className="flex flex-1 flex-col justify-center gap-4 p-4" data-testid="compact-playbook-running">
        <section className="rounded-lg border bg-card p-4 shadow-sm" aria-live="polite">
          <div className="flex items-start gap-3">
            <div className="flex size-9 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
              <Spinner className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Playbook running</p>
              <h2 className="truncate font-semibold">{rootRun.playbook.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{getPlaybookRunStatusLabel(activeRun.status)}</p>
              {runningToolName ? (
                <p className="mt-2 text-sm">
                  <span className="text-muted-foreground">Current step:</span> {runningToolDescription}
                </p>
              ) : null}
              <p className="mt-1 text-xs text-muted-foreground">{formatElapsedTime(rootRun.startedAt, now)}</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={onShowLiveTranscript}>
              <Icon icon="forum" />
              Show live transcript
            </Button>
            {!isReadOnly ? (
              <Button type="button" size="sm" variant="outline" onClick={handleStop} disabled={isStopping}>
                <Icon icon="stop" />
                {isStopping ? "Stopping..." : "Stop playbook"}
              </Button>
            ) : null}
          </div>
        </section>
      </main>
    </ChatScrollWrapper>
  );
};
